import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

import { MethodTypeItem } from './method-type-list.state';

/**
 * Context for the manager instance — which case type owns this assigned list,
 * and whether that case type is already persisted (a staged, brand-new case type
 * has no persisted method-type associations to seed, so its assigned list is
 * purely the session's staged assignments).
 */
export interface MethodTypeManagerContext {
  caseTypeId: string | undefined;
  persisted: boolean;
}

interface AssignedMethodTypesStateModel {
  caseTypeId: string | undefined;
  caseTypePersisted: boolean;
  /*
   * The full set of method types assigned to the case type in the database,
   * seeded once by the consumer (the case-type editor's load). The combined set
   * is just a view over this plus the session's staged changes.
   */
  persistedAssigned: MethodTypeItem[];
  persistedLoaded: boolean;
  /*
   * Method types assigned in this session but not yet persisted — keyed by id.
   * Includes brand-new method types created via the editor dialog and persisted
   * method types that were not in `persistedAssigned`.
   */
  stagedAssigned: Record<string, MethodTypeItem>;
  /*
   * Persisted-assigned method types un-assigned in this session. They are
   * filtered out of the combined set until the case-type save reconciles them.
   */
  stagedUnassignedIds: string[];
}

const INITIAL_STATE: AssignedMethodTypesStateModel = {
  caseTypeId: undefined,
  caseTypePersisted: false,
  persistedAssigned: [],
  persistedLoaded: false,
  stagedAssigned: {},
  stagedUnassignedIds: [],
};

/**
 * Shared, staged set of method types assigned to the case type being edited —
 * the right list of the method-type manager. The association is an array of ids
 * on the case type, exactly like the resource-manager assignment; the "ground
 * truth" is the combined set computed here (persisted − staged-unassigned ∪
 * staged-assigned).
 *
 * A plain `ComponentStore` with NO companion Effects (the assigned list is
 * seeded by the consumer and never paginates — `AssignedResourcesState`
 * precedent). One instance is provided by the OUTERMOST consumer (the case-type
 * editor page) and shared down the dialog stack so staged assignments survive a
 * manager unmount and feed the future save cascade. Deliberately NOT part of
 * `methodTypeManagerProviders`.
 */
@Injectable()
export class AssignedMethodTypesState extends ComponentStore<AssignedMethodTypesStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly caseTypeId$ = this.select((s) => s.caseTypeId);
  public readonly caseTypePersisted$ = this.select((s) => s.caseTypePersisted);
  public readonly persistedAssigned$ = this.select((s) => s.persistedAssigned);
  public readonly persistedLoaded$ = this.select((s) => s.persistedLoaded);
  public readonly stagedAssigned$ = this.select((s) => s.stagedAssigned);
  public readonly stagedUnassignedIds$ = this.select((s) => s.stagedUnassignedIds);

  /** The full assigned set (persisted − unassigned ∪ staged), unsorted. */
  public readonly combinedAssigned$ = this.select((s) => this.combine(s));

  /** Set of currently-assigned ids — the source the left list derives its flag from. */
  public readonly assignedIds$ = this.select((s) => new Set(this.combine(s).map((m) => m.id)));

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setContext = this.updater((state, ctx: MethodTypeManagerContext) => ({
    ...state,
    caseTypeId: ctx.caseTypeId,
    caseTypePersisted: ctx.persisted,
  }));

  public readonly setPersistedAssigned = this.updater((state, persistedAssigned: MethodTypeItem[]) => ({
    ...state,
    persistedAssigned,
    persistedLoaded: true,
  }));

  /** Convenience for the consumer/stories: seed context + persisted assignments in one shot. */
  public readonly seed = this.updater(
    (state, payload: { context: MethodTypeManagerContext; assigned: MethodTypeItem[] }) => ({
      ...state,
      caseTypeId: payload.context.caseTypeId,
      caseTypePersisted: payload.context.persisted,
      persistedAssigned: payload.assigned,
      persistedLoaded: true,
    })
  );

  /**
   * Stage a method type as assigned. A persisted-assigned method type only needs
   * un-marking as removed; a not-currently-assigned one (catalog pick or
   * brand-new draft) is recorded with its data so the combined set can render it.
   */
  public readonly assign = this.updater((state, methodType: MethodTypeItem) => {
    const stagedUnassignedIds = state.stagedUnassignedIds.filter((id) => id !== methodType.id);
    const persistedHas = state.persistedAssigned.some((m) => m.id === methodType.id);
    const stagedAssigned = persistedHas
      ? state.stagedAssigned
      : { ...state.stagedAssigned, [methodType.id]: methodType };
    return { ...state, stagedAssigned, stagedUnassignedIds };
  });

  /** Un-stage a method type. Persisted ones are recorded as removed; staged ones just drop. */
  public readonly unassign = this.updater((state, id: string) => {
    const stagedAssigned = { ...state.stagedAssigned };
    delete stagedAssigned[id];
    const persistedHas = state.persistedAssigned.some((m) => m.id === id);
    const stagedUnassignedIds =
      persistedHas && !state.stagedUnassignedIds.includes(id)
        ? [...state.stagedUnassignedIds, id]
        : state.stagedUnassignedIds;
    return { ...state, stagedAssigned, stagedUnassignedIds };
  });

  /**
   * Patch an assigned method type's data (e.g. name) after it was edited in the
   * dialog, so the right list reflects the edit without changing assignment.
   */
  public readonly updateAssigned = this.updater((state, methodType: MethodTypeItem) => {
    const persistedAssigned = state.persistedAssigned.map((m) =>
      m.id === methodType.id ? { ...m, ...methodType } : m
    );
    const stagedAssigned = state.stagedAssigned[methodType.id]
      ? { ...state.stagedAssigned, [methodType.id]: { ...state.stagedAssigned[methodType.id], ...methodType } }
      : state.stagedAssigned;
    return { ...state, persistedAssigned, stagedAssigned };
  });

  /**
   * After persistence, swap client temp ids for the REAL ids the saves returned
   * (`AssignedPipelinesState.remapIds` parity): `stagedAssigned` entries are
   * re-keyed and their `id` patched. Ids absent from the map stay put;
   * `stagedUnassignedIds` never needs remapping (only persisted method types —
   * real DB ids — ever land there).
   */
  public readonly remapIds = this.updater((state, idMap: Record<string, string>) => {
    const stagedAssigned: Record<string, MethodTypeItem> = {};
    for (const id of Object.keys(state.stagedAssigned)) {
      const newId = idMap[id] ?? id;
      stagedAssigned[newId] = { ...state.stagedAssigned[id], id: newId };
    }
    return { ...state, stagedAssigned };
  });

  public readonly clearStaging = this.updater((state) => ({
    ...state,
    stagedAssigned: {},
    stagedUnassignedIds: [],
  }));

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  // ============================================================================
  // Helpers
  // ============================================================================

  private combine(model: AssignedMethodTypesStateModel): MethodTypeItem[] {
    const unassigned = new Set(model.stagedUnassignedIds);
    const byId = new Map<string, MethodTypeItem>();
    for (const m of model.persistedAssigned) {
      if (!unassigned.has(m.id)) byId.set(m.id, m);
    }
    for (const id of Object.keys(model.stagedAssigned)) {
      byId.set(id, model.stagedAssigned[id]);
    }
    return Array.from(byId.values());
  }
}
